'use client';

import { useState } from 'react';
import { useResumeBuilderStore } from '@/stores/resume-builder-store';
import { Input } from '@/components/ui/forms/input';
import { Label } from '@/components/ui/forms/label';
import { Textarea } from '@/components/ui/forms/textarea';
import { Button } from '@/components/ui/forms/button';
import { Checkbox } from '@/components/ui/forms/checkbox';
import { Plus, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import { generateId } from '../../utils';
import ImproveTextControl from '../ImproveTextControl';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/layout/collapsible';
import { WithCommentIndicator } from '@/components/features/resume/AnalysisCommentIndicator';
import type { ResumeAnalysisSuggestion } from '@/lib/resume/analysis';

interface EmploymentSectionProps {
  guestMode?: boolean;
  onGuestAction?: () => void;
  /** Analysis suggestions targeting each employment entry's responsibilities, keyed by employment id. */
  commentSuggestionMap?: Map<string, ResumeAnalysisSuggestion[]>;
  onApplyCommentSuggestion?: (suggestion: ResumeAnalysisSuggestion) => void;
  applyingSuggestionId?: string | null;
}

export default function EmploymentSection({
  guestMode,
  onGuestAction,
  commentSuggestionMap,
  onApplyCommentSuggestion,
  applyingSuggestionId,
}: EmploymentSectionProps) {
  const { resume, addEmployment, updateEmployment, removeEmployment } = useResumeBuilderStore();
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  if (!resume) return null;

  const { employment } = resume.content;

  const handleAdd = () => {
    const id = generateId();
    addEmployment({
      id,
      title: '',
      company: '',
      location: '',
      startDate: '',
      endDate: '',
      isCurrentlyWorking: false,
      responsibilities: '',
    });
    setExpandedIds((prev) => new Set([...prev, id]));
  };

  const toggleExpanded = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // Sort by start date descending
  const sortedEmployment = [...employment].sort((a, b) => {
    if (!a.startDate) return 1;
    if (!b.startDate) return -1;
    return b.startDate.localeCompare(a.startDate);
  });

  return (
    <div className="space-y-4">
      {sortedEmployment.length === 0 ? (
        <p className="text-body text-muted-foreground">
          Add your work experience, starting with your most recent position.
        </p>
      ) : (
        <div className="space-y-3">
          {sortedEmployment.map((emp, index) => (
            <Collapsible
              key={`${emp.id}-${index}`}
              open={expandedIds.has(emp.id)}
              onOpenChange={() => toggleExpanded(emp.id)}
            >
              <div className="rounded-lg border border-border bg-card">
                <CollapsibleTrigger asChild>
                  <button
                    type="button"
                    className="flex w-full items-center justify-between p-4 text-left"
                  >
                    <div className="flex-1">
                      <div className="text-label">
                        {emp.title || 'New Position'}
                        {emp.company && (
                          <span className="text-body text-muted-foreground"> at {emp.company}</span>
                        )}
                      </div>
                      {emp.startDate && (
                        <div className="text-body-sm text-muted-foreground">
                          {formatDate(emp.startDate)} -{' '}
                          {emp.isCurrentlyWorking ? 'Present' : formatDate(emp.endDate || '')}
                        </div>
                      )}
                    </div>
                    {expandedIds.has(emp.id) ? (
                      <ChevronUp className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    )}
                  </button>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="space-y-4 border-t border-border p-4">
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="space-y-2">
                        <Label>Job Title *</Label>
                        <Input
                          value={emp.title}
                          onChange={(e) => updateEmployment(emp.id, { title: e.target.value })}
                          placeholder="Senior Software Engineer"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Company *</Label>
                        <Input
                          value={emp.company}
                          onChange={(e) => updateEmployment(emp.id, { company: e.target.value })}
                          placeholder="Acme Inc."
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Location</Label>
                      <Input
                        value={emp.location || ''}
                        onChange={(e) => updateEmployment(emp.id, { location: e.target.value })}
                        placeholder="San Francisco, CA"
                      />
                    </div>

                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="space-y-2">
                        <Label>Start Date *</Label>
                        <Input
                          type="month"
                          value={emp.startDate}
                          onChange={(e) => updateEmployment(emp.id, { startDate: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>End Date</Label>
                        <Input
                          type="month"
                          value={emp.endDate || ''}
                          onChange={(e) => updateEmployment(emp.id, { endDate: e.target.value })}
                          disabled={emp.isCurrentlyWorking}
                        />
                      </div>
                    </div>

                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id={`current-${emp.id}`}
                        checked={emp.isCurrentlyWorking}
                        onCheckedChange={(checked) =>
                          updateEmployment(emp.id, { isCurrentlyWorking: checked as boolean })
                        }
                      />
                      <label htmlFor={`current-${emp.id}`} className="text-body">
                        I currently work here
                      </label>
                    </div>

                    <WithCommentIndicator
                      suggestions={commentSuggestionMap?.get(emp.id) ?? []}
                      onApply={onApplyCommentSuggestion ?? (() => {})}
                      isApplying={applyingSuggestionId}
                    >
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label>Responsibilities & Achievements</Label>
                          <ImproveTextControl
                            text={emp.responsibilities || ''}
                            field="responsibilities"
                            context={{
                              jobTitle: emp.title,
                              company: emp.company,
                            }}
                            instructionPlaceholder="e.g., Add more quantifiable metrics, use stronger action verbs..."
                            onImproved={(improvedText) =>
                              updateEmployment(emp.id, { responsibilities: improvedText })
                            }
                            guestMode={guestMode}
                            onGuestAction={onGuestAction}
                          />
                        </div>
                        <Textarea
                          value={typeof emp.responsibilities === 'string' ? emp.responsibilities : ''}
                          onChange={(e) =>
                            updateEmployment(emp.id, { responsibilities: e.target.value })
                          }
                          placeholder="- Led a team of 5 engineers to deliver...&#10;- Increased system performance by 40%...&#10;- Implemented new features that..."
                          rows={6}
                        />
                        <p className="text-caption text-muted-foreground">
                          Use bullet points to list your key responsibilities and achievements.
                        </p>
                      </div>
                    </WithCommentIndicator>

                    <div className="flex justify-end">
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        onClick={() => removeEmployment(emp.id)}
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        Remove
                      </Button>
                    </div>
                  </div>
                </CollapsibleContent>
              </div>
            </Collapsible>
          ))}
        </div>
      )}

      <Button type="button" variant="outline" onClick={handleAdd}>
        <Plus className="mr-2 h-4 w-4" />
        Add Employment
      </Button>
    </div>
  );
}

function formatDate(dateStr: string): string {
  if (!dateStr) return '';
  const [year, month] = dateStr.split('-');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[parseInt(month) - 1]} ${year}`;
}
